import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import type {
  LibraTicket,
  LibraTicketGroup,
  SiteRoiAssumptionsValue,
} from 'features/libra/ticket/libraTicketsApi'
import {
  TAR_ROI_GLOBAL_STORAGE_KEY,
  TAR_ROI_GROUP_DATA_KEY,
} from 'features/libra/ticket/libraTicketsApi'
import { ELIGIBILITY_STATUS } from 'features/libra/types/eligibility'
import groupBy from 'lodash/groupBy'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'

const UNKNOWN_SITE_KEY = '__unknown_site__'

export const INSPECTABLE_AREA_M2_FIELD = 'inspectable_area_m2'
export const NII_ROI_EQUIPMENT_PREP_AED_FIELD =
  'nii_roi_equipment_prep_mob_demob_aed'
export const NII_ROI_INSPECTION_AED_FIELD = 'nii_roi_inspection_aed'
export const NII_ROI_SCAFFOLDING_AED_FIELD = 'nii_roi_scaffolding_aed'

export type NiiCalculatorSavingsCategory =
  | 'equipment'
  | 'inspection'
  | 'scaffolding'
  | 'hse'
  | 'production'

export type NiiCalculatorSavingsBreakdown = Record<
  NiiCalculatorSavingsCategory,
  number
> & {
  total: number
}

export type ResolvedNiiCalculatorAssumptions = {
  traditionalInspectionTeamPerAsset: number
  equipmentPrepMobDemobAedPerAsset: number
  traditionalInspectionAedPerAsset: number
  niiInspectionAedPerAsset: number
  scaffoldingAedPerAsset: number
  hseIncidentProbPerPersonPerTar: number
  hseDownsideAed: number
  criticalPathReductionPercent: number
  tarDurationDays: number
  dailyProductionAed: number
  inspectionOnCriticalPath: boolean
}

export const DEFAULT_RESOLVED_NII_CALCULATOR_ASSUMPTIONS: ResolvedNiiCalculatorAssumptions =
  {
    traditionalInspectionTeamPerAsset: 8,
    equipmentPrepMobDemobAedPerAsset: 200_000,
    traditionalInspectionAedPerAsset: 15_000,
    niiInspectionAedPerAsset: 220_350,
    scaffoldingAedPerAsset: 183_625,
    hseIncidentProbPerPersonPerTar: 0.001,
    hseDownsideAed: 1_500_000,
    criticalPathReductionPercent: 5,
    tarDurationDays: 0,
    dailyProductionAed: 0,
    inspectionOnCriticalPath: false,
  }

export const NII_CALCULATOR_CATEGORY_COLORS: Record<
  NiiCalculatorSavingsCategory,
  string
> = {
  equipment: '#4472C4',
  inspection: '#5B9BD5',
  scaffolding: '#ED7D31',
  hse: '#A78BFA',
  production: '#70AD47',
}

export const NII_CALCULATOR_CATEGORY_LABELS: Record<
  NiiCalculatorSavingsCategory,
  string
> = {
  equipment: 'Equip prep / mob / demob',
  inspection: 'Inspection',
  scaffolding: 'Scaffolding',
  hse: 'HSE',
  production: 'Production',
}

export type NiiRoiAssetOverrideFields = {
  equipmentPrepMobDemobAedPerAsset: number | undefined
  niiInspectionAedPerAsset: number | undefined
  scaffoldingAedPerAsset: number | undefined
}

export type NiiRoiAssetCostInputs = {
  equipmentPrepMobDemobAedPerAsset: number
  traditionalInspectionAedPerAsset: number
  niiInspectionAedPerAsset: number
  scaffoldingAedPerAsset: number
}

export function formatAedFull(value: number): string {
  if (value === 0 || Number.isNaN(value)) {
    return 'AED 0'
  }
  const rounded = Math.round(value * 100) / 100
  return `AED ${rounded.toLocaleString('en-GB', { maximumFractionDigits: 2 })}`
}

type SiteRoiMap =
  | Readonly<Record<string, SiteRoiAssumptionsValue | undefined>>
  | undefined

export function getNiiCalculatorTarRoiBySiteFromGroup(
  group: LibraTicketGroup
): SiteRoiMap {
  return group[TAR_ROI_GROUP_DATA_KEY]
}

function storedHseProb(
  stored: SiteRoiAssumptionsValue | undefined
): number | undefined {
  return (
    stored?.hseIncidentProbPerPersonPerTar ??
    stored?.hseIncidentProbPerPersonPerDay
  )
}

function pickNumber(
  siteStored: SiteRoiAssumptionsValue | undefined,
  globalStored: SiteRoiAssumptionsValue | undefined,
  key: keyof SiteRoiAssumptionsValue,
  fallback: number
): number {
  const siteValue = siteStored?.[key]
  if (typeof siteValue === 'number') return siteValue
  const globalValue = globalStored?.[key]
  if (typeof globalValue === 'number') return globalValue
  return fallback
}

export function mergeNiiCalculatorAssumptionsForSite(
  globalStored: SiteRoiAssumptionsValue | undefined,
  siteStored: SiteRoiAssumptionsValue | undefined
): ResolvedNiiCalculatorAssumptions {
  const d = DEFAULT_RESOLVED_NII_CALCULATOR_ASSUMPTIONS
  return {
    traditionalInspectionTeamPerAsset: pickNumber(
      siteStored,
      globalStored,
      'traditionalInspectionTeamPerAsset',
      d.traditionalInspectionTeamPerAsset
    ),
    equipmentPrepMobDemobAedPerAsset: pickNumber(
      siteStored,
      globalStored,
      'equipmentPrepMobDemobAedPerAsset',
      d.equipmentPrepMobDemobAedPerAsset
    ),
    traditionalInspectionAedPerAsset: pickNumber(
      siteStored,
      globalStored,
      'traditionalInspectionAedPerAsset',
      d.traditionalInspectionAedPerAsset
    ),
    niiInspectionAedPerAsset: pickNumber(
      siteStored,
      globalStored,
      'niiInspectionAedPerAsset',
      d.niiInspectionAedPerAsset
    ),
    scaffoldingAedPerAsset: pickNumber(
      siteStored,
      globalStored,
      'scaffoldingAedPerAsset',
      d.scaffoldingAedPerAsset
    ),
    hseIncidentProbPerPersonPerTar:
      storedHseProb(siteStored) ??
      storedHseProb(globalStored) ??
      d.hseIncidentProbPerPersonPerTar,
    hseDownsideAed: pickNumber(
      siteStored,
      globalStored,
      'hseDownsideAed',
      d.hseDownsideAed
    ),
    criticalPathReductionPercent: pickNumber(
      siteStored,
      globalStored,
      'criticalPathReductionPercent',
      d.criticalPathReductionPercent
    ),
    tarDurationDays: pickNumber(
      siteStored,
      undefined,
      'tarDurationDays',
      d.tarDurationDays
    ),
    dailyProductionAed: pickNumber(
      siteStored,
      undefined,
      'dailyProductionAed',
      d.dailyProductionAed
    ),
    inspectionOnCriticalPath:
      siteStored?.inspectionOnCriticalPath ?? d.inspectionOnCriticalPath,
  }
}

export function getTicketAreaM2(ticket: LibraTicket): number {
  return (
    unwrapFieldValue<number>(ticket.fieldValues[INSPECTABLE_AREA_M2_FIELD]) ?? 0
  )
}

export function readNiiRoiAssetOverrides(
  ticket: LibraTicket
): NiiRoiAssetOverrideFields {
  const equipmentPrepMobDemobAedPerAsset = unwrapFieldValue<number | null>(
    ticket.fieldValues[NII_ROI_EQUIPMENT_PREP_AED_FIELD]
  )
  const niiInspectionAedPerAsset = unwrapFieldValue<number | null>(
    ticket.fieldValues[NII_ROI_INSPECTION_AED_FIELD]
  )
  const scaffoldingAedPerAsset = unwrapFieldValue<number | null>(
    ticket.fieldValues[NII_ROI_SCAFFOLDING_AED_FIELD]
  )
  return {
    equipmentPrepMobDemobAedPerAsset:
      typeof equipmentPrepMobDemobAedPerAsset === 'number'
        ? equipmentPrepMobDemobAedPerAsset
        : undefined,
    niiInspectionAedPerAsset:
      typeof niiInspectionAedPerAsset === 'number'
        ? niiInspectionAedPerAsset
        : undefined,
    scaffoldingAedPerAsset:
      typeof scaffoldingAedPerAsset === 'number'
        ? scaffoldingAedPerAsset
        : undefined,
  }
}

export function resolveNiiRoiAssetCostInputs(
  ticket: LibraTicket,
  assumptions: ResolvedNiiCalculatorAssumptions
): NiiRoiAssetCostInputs {
  const overrides = readNiiRoiAssetOverrides(ticket)
  return {
    equipmentPrepMobDemobAedPerAsset:
      overrides.equipmentPrepMobDemobAedPerAsset ??
      assumptions.equipmentPrepMobDemobAedPerAsset,
    traditionalInspectionAedPerAsset:
      assumptions.traditionalInspectionAedPerAsset,
    niiInspectionAedPerAsset:
      overrides.niiInspectionAedPerAsset ??
      assumptions.niiInspectionAedPerAsset,
    scaffoldingAedPerAsset:
      overrides.scaffoldingAedPerAsset ?? assumptions.scaffoldingAedPerAsset,
  }
}

function isNiiEligibleForRoi(ticket: LibraTicket): boolean {
  if (!ticket.workflowComplete) return false
  return ticket.fieldValues?.eligibility?.value === ELIGIBILITY_STATUS.ELIGIBLE
}

function pickRoiEligibleTickets(
  tickets: ReadonlyArray<LibraTicket>
): LibraTicket[] {
  return tickets.filter(isNiiEligibleForRoi)
}

function ticketUnitKey(ticket: LibraTicket): string {
  return typeof ticket.tags.unit === 'string' && ticket.tags.unit
    ? ticket.tags.unit
    : ticket.id
}

function ticketSiteKey(ticket: LibraTicket): string {
  return typeof ticket.tags.site === 'string' && ticket.tags.site
    ? ticket.tags.site
    : UNKNOWN_SITE_KEY
}

function countUniqueUnits(tickets: ReadonlyArray<LibraTicket>): number {
  return uniq(tickets.map(ticketUnitKey)).length
}

function isTicketCompleteForCalculator(ticket: LibraTicket): boolean {
  return ticket.workflowComplete || ticket.ticketStatus === 'approved'
}

function isTicketInProgressForCalculator(ticket: LibraTicket): boolean {
  const status = ticket.ticketStatus.trim().toLowerCase()
  return status === 'pending_review' || status === 'in_progress'
}

export type NiiCalculatorSiteSummary = {
  siteId: string
  reviewedAssets: number
  eligibleAssets: number
  notStartedAssets: number
  inProgressAssets: number
  completeAssets: number
  savingPerEligibleAsset: number
  assumptions: ResolvedNiiCalculatorAssumptions
  breakdown: NiiCalculatorSavingsBreakdown
}

export type NiiCalculatorAggregate = {
  byCategory: NiiCalculatorSavingsBreakdown
  reviewedAssets: number
  eligibleAssets: number
  notStartedAssets: number
  inProgressAssets: number
  completeAssets: number
  savingPerEligibleAsset: number
  bySite: ReadonlyArray<NiiCalculatorSiteSummary>
}

function emptyCalculatorBreakdown(): NiiCalculatorSavingsBreakdown {
  return {
    equipment: 0,
    inspection: 0,
    scaffolding: 0,
    hse: 0,
    production: 0,
    total: 0,
  }
}

export function computeNiiCalculatorSavingsForSite(
  tickets: ReadonlyArray<LibraTicket>,
  assumptions: ResolvedNiiCalculatorAssumptions
): NiiCalculatorSiteSummary {
  const roiTickets = pickRoiEligibleTickets(tickets)
  const uniqueRoiTickets = uniqBy(roiTickets, ticketUnitKey)
  const eligibleAssets = uniqueRoiTickets.length
  const reviewedAssets = countUniqueUnits(tickets)
  const completeAssets = countUniqueUnits(
    tickets.filter(isTicketCompleteForCalculator)
  )
  const inProgressAssets = countUniqueUnits(
    tickets.filter(isTicketInProgressForCalculator)
  )
  const notStartedAssets = Math.max(
    0,
    reviewedAssets - completeAssets - inProgressAssets
  )

  const assetCostRows = uniqueRoiTickets.map((ticket) =>
    resolveNiiRoiAssetCostInputs(ticket, assumptions)
  )
  const equipment = assetCostRows.reduce(
    (sum, costs) => sum + costs.equipmentPrepMobDemobAedPerAsset,
    0
  )
  const inspection = assetCostRows.reduce(
    (sum, costs) =>
      sum +
      (costs.traditionalInspectionAedPerAsset - costs.niiInspectionAedPerAsset),
    0
  )
  const scaffolding = assetCostRows.reduce(
    (sum, costs) => sum + costs.scaffoldingAedPerAsset,
    0
  )
  const hse =
    eligibleAssets *
    assumptions.traditionalInspectionTeamPerAsset *
    assumptions.hseIncidentProbPerPersonPerTar *
    assumptions.hseDownsideAed
  const production = assumptions.inspectionOnCriticalPath
    ? assumptions.tarDurationDays *
      (assumptions.criticalPathReductionPercent / 100) *
      assumptions.dailyProductionAed
    : 0
  const total = equipment + inspection + scaffolding + hse + production

  return {
    siteId: tickets[0] ? ticketSiteKey(tickets[0]) : UNKNOWN_SITE_KEY,
    reviewedAssets,
    eligibleAssets,
    notStartedAssets,
    inProgressAssets,
    completeAssets,
    savingPerEligibleAsset: eligibleAssets > 0 ? total / eligibleAssets : 0,
    assumptions,
    breakdown: {
      equipment,
      inspection,
      scaffolding,
      hse,
      production,
      total,
    },
  }
}

export function aggregateNiiCalculatorSavings(
  tickets: ReadonlyArray<LibraTicket>,
  tarRoiBySite: SiteRoiMap
): NiiCalculatorAggregate {
  const globalStored = tarRoiBySite?.[TAR_ROI_GLOBAL_STORAGE_KEY]
  const bySiteGrouped = groupBy(tickets, ticketSiteKey)
  const bySite = Object.entries(bySiteGrouped)
    .flatMap(([siteId, siteTickets]) => {
      if (!siteTickets?.length) return []
      const siteStored =
        siteId === UNKNOWN_SITE_KEY ? undefined : tarRoiBySite?.[siteId]
      return [
        computeNiiCalculatorSavingsForSite(
          siteTickets,
          mergeNiiCalculatorAssumptionsForSite(globalStored, siteStored)
        ),
      ]
    })
    .toSorted((a, b) => a.siteId.localeCompare(b.siteId))

  const byCategory = emptyCalculatorBreakdown()
  let reviewedAssets = 0
  let eligibleAssets = 0
  let notStartedAssets = 0
  let inProgressAssets = 0
  let completeAssets = 0

  for (const site of bySite) {
    byCategory.equipment += site.breakdown.equipment
    byCategory.inspection += site.breakdown.inspection
    byCategory.scaffolding += site.breakdown.scaffolding
    byCategory.hse += site.breakdown.hse
    byCategory.production += site.breakdown.production
    reviewedAssets += site.reviewedAssets
    eligibleAssets += site.eligibleAssets
    notStartedAssets += site.notStartedAssets
    inProgressAssets += site.inProgressAssets
    completeAssets += site.completeAssets
  }

  byCategory.total =
    byCategory.equipment +
    byCategory.inspection +
    byCategory.scaffolding +
    byCategory.hse +
    byCategory.production

  return {
    byCategory,
    reviewedAssets,
    eligibleAssets,
    notStartedAssets,
    inProgressAssets,
    completeAssets,
    savingPerEligibleAsset:
      eligibleAssets > 0 ? byCategory.total / eligibleAssets : 0,
    bySite,
  }
}

export function getDistinctSiteIdsFromTickets(
  tickets: ReadonlyArray<LibraTicket>
): string[] {
  return uniq(
    tickets
      .map((t) => t.tags.site)
      .filter((s): s is string => typeof s === 'string' && s.length > 0)
  )
}
